/**
 * DOM Utility Functions
 * Helper functions for common DOM operations
 */

/**
 * Safely query a single element
 * @param {string} selector - CSS selector
 * @param {Element} parent - Parent element (optional)
 * @returns {Element|null}
 */
export function $(selector, parent = document) {
    return parent.querySelector(selector);
}

/**
 * Safely query multiple elements
 * @param {string} selector - CSS selector
 * @param {Element} parent - Parent element (optional)
 * @returns {NodeList}
 */
export function $$(selector, parent = document) {
    return parent.querySelectorAll(selector);
}

/**
 * Add event listener to element(s)
 * @param {Element|NodeList} elements - Element or elements to attach listener to
 * @param {string} event - Event name
 * @param {Function} handler - Event handler function
 */
export function on(elements, event, handler) {
    if (elements instanceof NodeList) {
        elements.forEach(el => el.addEventListener(event, handler));
    } else if (elements) {
        elements.addEventListener(event, handler);
    }
}

/**
 * Remove event listener from element(s)
 * @param {Element|NodeList} elements - Element or elements to remove listener from
 * @param {string} event - Event name
 * @param {Function} handler - Event handler function
 */
export function off(elements, event, handler) {
    if (elements instanceof NodeList) {
        elements.forEach(el => el.removeEventListener(event, handler));
    } else if (elements) {
        elements.removeEventListener(event, handler);
    }
}

/**
 * Toggle class on element
 * @param {Element} element - Target element
 * @param {string} className - Class name to toggle
 * @param {boolean} force - Force add or remove
 */
export function toggleClass(element, className, force) {
    if (!element) return;
    if (force !== undefined) {
        element.classList.toggle(className, force);
    } else {
        element.classList.toggle(className);
    }
}

/**
 * Add class to element
 * @param {Element} element - Target element
 * @param {string} className - Class name to add
 */
export function addClass(element, className) {
    if (!element) return;
    element.classList.add(className);
}

/**
 * Remove class from element
 * @param {Element} element - Target element
 * @param {string} className - Class name to remove
 */
export function removeClass(element, className) {
    if (!element) return;
    element.classList.remove(className);
}

/**
 * Check if element has class
 * @param {Element} element - Target element
 * @param {string} className - Class name to check
 * @returns {boolean}
 */
export function hasClass(element, className) {
    if (!element) return false;
    return element.classList.contains(className);
}

/**
 * Get element's offset from top of page
 * @param {Element} element - Target element
 * @returns {number}
 */
export function getOffsetTop(element) {
    if (!element) return 0;
    return element.offsetTop;
}

/**
 * Smooth scroll to element or position
 * @param {Element|number} target - Target element or Y position
 * @param {number} offset - Additional offset (optional)
 */
export function smoothScroll(target, offset = 0) {
    const position = typeof target === 'number'
        ? target
        : getOffsetTop(target) - offset;

    window.scrollTo({
        top: position,
        behavior: 'smooth'
    });
}

/**
 * Check if element is in viewport
 * @param {Element} element - Target element
 * @returns {boolean}
 */
export function isInViewport(element) {
    if (!element) return false;
    const rect = element.getBoundingClientRect();
    return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
}
